import {
  INVALID_CARD_ERROR,
  IS_API_MODE,
  ONLY_SUBSCRIBE_PROCESS,
  PAYMENT_METHOD_CHANGE_PROCESS,
  TIER_ID,
} from "@/shared/lib/token/token"
import { ApiSubscription, ApiTierIdEnum } from "./subscription.types"

export const SUBSCRIPTION_CONFLICT_REASON = {
  SubscriptionAlreadyActive: "SUBSCRIPTION_ALREADY_ACTIVE",
  SubscriptionActivationPending: "SUBSCRIPTION_ACTIVATION_PENDING",
  OtcContractActive: "OTC_CONTRACT_ACTIVE",
  OtcContractScheduled: "OTC_CONTRACT_SCHEDULED",
  LegacyConflict: "SUBSCRIPTION_CONFLICT",
} as const

export type SubscriptionConflictReason =
  (typeof SUBSCRIPTION_CONFLICT_REASON)[keyof typeof SUBSCRIPTION_CONFLICT_REASON]

const SUBSCRIPTION_CONFLICT_REASONS = new Set<string>(
  Object.values(SUBSCRIPTION_CONFLICT_REASON)
)

const ABANDONED_SUBSCRIPTION_ATTEMPT_KEYS = [
  IS_API_MODE,
  ONLY_SUBSCRIBE_PROCESS,
  TIER_ID,
  PAYMENT_METHOD_CHANGE_PROCESS,
  INVALID_CARD_ERROR,
] as const

export const clearAbandonedSubscriptionAttempt = (storage: {
  removeItem: (key: string) => void
}) => {
  ABANDONED_SUBSCRIPTION_ATTEMPT_KEYS.forEach((key) => storage.removeItem(key))
}

export const isSubscriptionConflictReason = (
  value: unknown
): value is SubscriptionConflictReason =>
  typeof value === "string" && SUBSCRIPTION_CONFLICT_REASONS.has(value)

/**
 * Resolve the task-management error payload into the stable UI contract.
 * `BadRequest` is retained as a rollout fallback for older backend versions.
 */
export const resolveSubscriptionConflictReason = (
  errorCode: unknown
): SubscriptionConflictReason | undefined => {
  if (isSubscriptionConflictReason(errorCode)) return errorCode
  if (errorCode === "BadRequest") {
    return SUBSCRIPTION_CONFLICT_REASON.LegacyConflict
  }
  return undefined
}

// An internal (staff) API grant stays "Active" on the server after its contract
// ends, because no batch ever visits it, and its key is refused until the grant
// is renewed. The server says whether a contract is in force, by its own clock,
// so the browser's clock never decides it.
export type InternalApiGrantState = "none" | "inForce" | "lapsed"

export const getInternalApiGrantState = (
  subscription: Pick<ApiSubscription, "tierId" | "contractInForce"> | undefined
): InternalApiGrantState => {
  if (!subscription || subscription.tierId !== ApiTierIdEnum.Internal) {
    return "none"
  }
  return subscription.contractInForce ? "inForce" : "lapsed"
}

// Offer staff a grant, or a renewal, only when the list has loaded and the server
// would accept it: no paid plan in force, and no grant in force. A paid plan whose
// period has ended does not stand in the way; the server closes it as it grants.
// While the list is loading or its last fetch failed, that is not known.
export const canOfferInternalApiGrant = ({
  isAdmin,
  listStatus,
  grantState,
  paidPlanInForce,
}: {
  isAdmin: boolean
  listStatus: "pending" | "error" | "success"
  grantState: InternalApiGrantState
  paidPlanInForce: boolean
}) =>
  isAdmin &&
  listStatus === "success" &&
  !paidPlanInForce &&
  grantState !== "inForce"

// Which live API subscription the settings row shows and acts on, when a staff
// grant and a paid plan are both live: whichever gives access now, the grant
// first; failing that, a lapsed grant, which can be renewed; failing that, the
// newest. A paid row without contractInForce counts as in force, as it always did.
export const selectDisplayedApiSubscription = <
  T extends Pick<ApiSubscription, "tierId" | "contractInForce">,
>(
  live: T[]
): T | undefined => {
  const grant = live.find((s) => s.tierId === ApiTierIdEnum.Internal)
  const paidInForce = live.find(
    (s) => s.tierId !== ApiTierIdEnum.Internal && s.contractInForce !== false
  )
  if (grant?.contractInForce) {
    return grant
  }
  return paidInForce ?? grant ?? live[0]
}

// The list as it stands once this view has issued a key, before the refetch
// brings the server's row: it must not go on saying there is no key, or the
// next click would take the path that skips the replace warning. The hint is
// the key's last four characters, as the server records it. When it was issued
// is the server's to say, so it stays unknown until the refetch; a replaced
// key's date must not stand for the new one either.
export const markApiKeyIssued = <
  T extends Pick<
    ApiSubscription,
    "id" | "hasApiKey" | "apiKeyHint" | "apiKeyIssuedAt"
  >,
>(
  list: T[] | undefined,
  subscriptionId: string,
  apiKey: string
): T[] | undefined =>
  list?.map((subscription) =>
    subscription.id === subscriptionId
      ? {
          ...subscription,
          hasApiKey: true,
          apiKeyHint: apiKey.slice(-4),
          apiKeyIssuedAt: null,
        }
      : subscription
  )

// The list once the server has refused a first key because one exists: the
// key was issued elsewhere, so which key and when are left to the refetch, but
// the next click must already go through the replace warning.
export const markApiKeyExists = <
  T extends Pick<ApiSubscription, "id" | "hasApiKey">,
>(
  list: T[] | undefined,
  subscriptionId: string
): T[] | undefined =>
  list?.map((subscription) =>
    subscription.id === subscriptionId
      ? { ...subscription, hasApiKey: true }
      : subscription
  )
